import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Download, School, User, Mail, BarChart3, Radio, LogOut, Trophy, FileDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/api/endpoints/auth';
import { ADMIN_BASE, ADMIN_LOGIN } from '@/config/routes';
import { SidebarBrand } from './SidebarBrand';
import toast from 'react-hot-toast';

const navItems: { to: string; label: string; icon: LucideIcon; end?: boolean }[] = [
  { to: `${ADMIN_BASE}/`, label: 'Dashboard', icon: Home, end: true },
  { to: `${ADMIN_BASE}/live`, label: 'Na żywo', icon: Radio },
  { to: `${ADMIN_BASE}/live#raporty`, label: 'Raporty CSV', icon: FileDown },
  { to: `${ADMIN_BASE}/import`, label: 'Import JSON', icon: Download },
  { to: `${ADMIN_BASE}/schools`, label: 'Szkoły', icon: School },
  { to: `${ADMIN_BASE}/participants`, label: 'Uczestnicy', icon: User },
  { to: `${ADMIN_BASE}/magic-links`, label: 'Magic Linki', icon: Mail },
  { to: `${ADMIN_BASE}/qualification`, label: 'Kwalifikacja E2', icon: Trophy },
  { to: `${ADMIN_BASE}/leaderboard`, label: 'Tabela wyników', icon: BarChart3 },
];

export const Sidebar = () => {
  const { sidebarOpen } = useUIStore();
  const clearAuthenticated = useAuthStore((s) => s.clearAuthenticated);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors – clear state and redirect regardless
    }
    clearAuthenticated();
    navigate(ADMIN_LOGIN, { replace: true });
    toast.success('Wylogowano pomyślnie');
  };

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200 shadow-sm transition-all duration-300',
        sidebarOpen ? 'w-64' : 'w-16',
      )}
    >
      <SidebarBrand sidebarOpen={sidebarOpen} />

      <nav className="flex-1 space-y-1 overflow-y-auto p-2">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-competition-accent/10 hover:text-competition-accent-dark',
              )
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            {sidebarOpen && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-gray-200 p-2">
        <button
          onClick={handleLogout}
          className={cn(
            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700',
          )}
          title="Wyloguj"
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {sidebarOpen && <span>Wyloguj</span>}
        </button>
      </div>
    </aside>
  );
};
